'use client';
import React, { useState, useEffect } from 'react';
import { Plus, X, MapPin, Edit2, Check } from 'lucide-react';
import { type Room } from '@/lib/claimStore';
import { fetchRooms, insertRoom, deleteRoom, updateRoom } from '@/lib/claimService';
import { useClaimContext } from '@/contexts/ClaimContext';
import ConfirmModal from '@/components/ui/ConfirmModal';
import { toast } from 'sonner';

export default function RoomsSection() {
  const { activeClaim } = useClaimContext();
  const claimId = activeClaim?.id;

  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);
  const [newRoomName, setNewRoomName] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const data = await fetchRooms(claimId);
      setRooms(data);
      setLoading(false);
    }
    load();
  }, [claimId]);

  const handleAdd = async () => {
    const trimmed = newRoomName.trim();
    if (!trimmed) return;
    if (rooms.some((r) => r.name.toLowerCase() === trimmed.toLowerCase())) {
      toast.error('A room with that name already exists');
      return;
    }
    const saved = await insertRoom(trimmed, claimId);
    if (saved) {
      setRooms((prev) => [...prev, saved]);
      setNewRoomName('');
      setIsAdding(false);
      toast.success(`"${trimmed}" added as a location`);
    } else {
      toast.error('Failed to add room');
    }
  };

  const startEdit = (room: Room) => {
    setEditingId(room.id);
    setEditName(room.name);
  };

  const saveEdit = async () => {
    const trimmed = editName.trim();
    if (!trimmed || !editingId) return;
    if (rooms.some((r) => r.id !== editingId && r.name.toLowerCase() === trimmed.toLowerCase())) {
      toast.error('A room with that name already exists');
      return;
    }
    const ok = await updateRoom(editingId, trimmed);
    if (ok) {
      setRooms((prev) => prev.map((r) => r.id === editingId ? { ...r, name: trimmed } : r));
      toast.success(`Room renamed to "${trimmed}"`);
    } else {
      toast.error('Failed to rename room');
    }
    setEditingId(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  const handleRemove = async (id: string, name: string) => {
    const ok = await deleteRoom(id);
    if (ok) {
      setRooms((prev) => prev.filter((r) => r.id !== id));
      toast.success(`"${name}" removed`);
    } else {
      toast.error('Failed to remove room');
    }
    setDeleteTarget(null);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-muted-foreground text-sm">
        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        Loading rooms…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MapPin size={16} className="text-primary" />
          <h2 className="text-base font-semibold text-foreground">Rooms &amp; Locations</h2>
          {activeClaim && (
            <span className="px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary rounded-full">
              {activeClaim.name}
            </span>
          )}
        </div>
        <button
          onClick={() => { setIsAdding(true); setEditingId(null); }}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-lg hover:bg-amber-700 transition-all duration-150 active:scale-95"
        >
          <Plus size={13} /> Add Room
        </button>
      </div>
      <p className="text-sm text-muted-foreground">
        Customize the list of rooms and locations for <strong>{activeClaim?.name ?? 'this claim'}</strong>. Items are organized by location in the export.
      </p>

      <div className="space-y-2">
        {rooms.map((room) => (
          <div
            key={room.id}
            className="flex items-center gap-3 p-3 bg-secondary/30 border border-border rounded-lg group"
          >
            <MapPin size={14} className="text-muted-foreground shrink-0" />

            {editingId === room.id ? (
              <div className="flex-1 flex items-center gap-2">
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="flex-1 px-2.5 py-1.5 text-sm border border-border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveEdit();
                    if (e.key === 'Escape') cancelEdit();
                  }}
                />
                <div className="flex gap-1">
                  <button
                    onClick={saveEdit}
                    disabled={!editName.trim()}
                    className="p-1.5 rounded hover:bg-positive-bg text-positive disabled:opacity-40 transition-colors"
                    title="Save"
                  >
                    <Check size={14} />
                  </button>
                  <button
                    onClick={cancelEdit}
                    className="p-1.5 rounded hover:bg-secondary text-muted-foreground transition-colors"
                    title="Cancel"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            ) : (
              <>
                <span className="flex-1 text-sm font-medium text-foreground">{room.name}</span>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => startEdit(room)}
                    className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-all duration-150 active:scale-95"
                    title={`Rename ${room.name}`}
                  >
                    <Edit2 size={13} />
                  </button>
                  <button
                    onClick={() => setDeleteTarget({ id: room.id, name: room.name })}
                    className="p-1.5 rounded hover:bg-negative-bg text-muted-foreground hover:text-negative transition-all duration-150 active:scale-95"
                    title={`Remove ${room.name}`}
                  >
                    <X size={13} />
                  </button>
                </div>
              </>
            )}
          </div>
        ))}

        {isAdding && (
          <div className="flex items-center gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg fade-in">
            <MapPin size={14} className="text-primary shrink-0" />
            <input
              type="text"
              value={newRoomName}
              onChange={(e) => setNewRoomName(e.target.value)}
              placeholder="Room name…"
              className="flex-1 px-2.5 py-1.5 text-sm border border-border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAdd();
                if (e.key === 'Escape') { setIsAdding(false); setNewRoomName(''); }
              }}
            />
            <div className="flex gap-1">
              <button
                onClick={handleAdd}
                disabled={!newRoomName.trim()}
                className="p-1.5 rounded hover:bg-positive-bg text-positive disabled:opacity-40 transition-colors"
                title="Add room"
              >
                <Check size={14} />
              </button>
              <button
                onClick={() => { setIsAdding(false); setNewRoomName(''); }}
                className="p-1.5 rounded hover:bg-secondary text-muted-foreground transition-colors"
                title="Cancel"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      <ConfirmModal
        open={deleteTarget !== null}
        title="Remove room?"
        description={`"${deleteTarget?.name}" will be removed from the list. Items assigned to this room will retain their location label but the room won't appear in the dropdown.`}
        confirmLabel="Remove Room"
        onConfirm={() => deleteTarget && handleRemove(deleteTarget.id, deleteTarget.name)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}